import { motion } from "framer-motion";
import styled, { css, keyframes } from "styled-components";

/*
 * O nama, v2 retheme na tokene: Archivo Black naslov, čiste kartice,
 * primary dugme bez gradijenta, brojevi u display fontu.
 *
 * Animacije: naslov na mount, sekcije na scroll (useReveal → $visible).
 */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* Reveal na scroll, bez $visible propa element je normalno vidljiv */
export const revealSafe = css`
  opacity: ${(p) => (p.$visible === false ? 0 : 1)};
  transform: translateY(${(p) => (p.$visible === false ? "26px" : "0")});
  transition:
    opacity 0.6s ease,
    transform 0.6s cubic-bezier(0.2, 0.8, 0.2, 1);

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transform: none;
    transition: none;
  }
`;

export { fadeUp };

export const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  padding: 2.5rem 1.5rem 5rem;
`;

export const PageHeader = styled.div`
  margin-bottom: 2.5rem;
`;

export const PageTitle = styled.h1`
  color: var(--text-primary);
  font-family: var(--font-display);
  font-size: clamp(40px, 6vw, 72px);
  line-height: 0.95;
  letter-spacing: -0.01em;
  text-transform: uppercase;
  margin-bottom: 1rem;
  animation: ${fadeUp} 0.65s cubic-bezier(0.2, 0.8, 0.2, 1) 0.08s both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const PageSubtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
  max-width: 600px;
  line-height: 1.6;
  margin: 0;
`;

export const Section = styled.section`
  background: var(--bg-surface);
  border-radius: var(--radius-card);
  padding: 2.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border);
  ${revealSafe}

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const SectionTitle = styled.h2`
  color: var(--text-primary);
  font-size: 1.4rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--brand);
  }
  @media (max-width: 768px) {
    text-align: center;
    flex-direction: column;
  }
`;

export const SectionText = styled.p`
  color: var(--text-secondary);
  font-size: 1rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  max-width: 720px;
  @media (max-width: 768px) {
    text-align: center;
  }
`;

export const TwoColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

/* Statistike, brojevi u display fontu, brand boja */
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin: 2rem 0 0;
  @media (max-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 1.75rem 1.25rem;
  text-align: center;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
  }
`;

export const StatIcon = styled.div`
  color: var(--brand);
  font-size: 1.75rem;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: center;
`;

export const StatNumber = styled.div`
  color: var(--brand);
  font-family: var(--font-display);
  font-size: 2.25rem;
  line-height: 1;
  margin-bottom: 0.5rem;
`;

export const StatLabel = styled.div`
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-weight: 500;
`;

export const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

export const TeamCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 2rem;
  text-align: center;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
  }
`;

export const TeamAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--brand-dim);
  border: 1px solid rgba(255, 107, 53, 0.45);
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-display);
  font-size: 1.75rem;
  color: var(--brand);
`;

export const TeamName = styled.h3`
  color: var(--text-primary);
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

export const TeamRole = styled.p`
  color: var(--brand);
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const TeamDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
`;

export const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

export const ContactCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 1.75rem;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
  }
`;

export const ContactIcon = styled.div`
  color: var(--brand);
  font-size: 1.75rem;
  margin-bottom: 1rem;
`;

export const ContactTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.1rem;
  margin-bottom: 1rem;
  font-weight: 700;
`;

export const ContactInfo = styled.div`
  color: var(--text-secondary);
  line-height: 1.6;

  a {
    color: var(--brand);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

export const ContactForm = styled.form`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 1.75rem;
  margin-top: 2rem;
`;

export const FormTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.25rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: var(--brand);
  }
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

export const FormLabel = styled.label`
  display: block;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.88rem;
  margin-bottom: 0.45rem;
`;

export const FormInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: var(--bg-app);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  height: 44px;
  padding: 0 0.9rem;
  color: var(--text-primary);
  font-size: 0.92rem;
  font-family: var(--font-body);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  background: var(--bg-app);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-control);
  padding: 0.7rem 0.9rem;
  color: var(--text-primary);
  font-size: 0.92rem;
  font-family: var(--font-body);
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--brand);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

export const SubmitButton = styled.button`
  background: var(--brand);
  color: #ffffff;
  border: none;
  border-radius: var(--radius-control);
  height: 50px;
  padding: 0 1.75rem;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 0.18s ease,
    transform 0.18s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    background: var(--brand-hover);
    transform: translateY(-1px);
  }

  &:disabled {
    background: var(--bg-elevated);
    color: var(--text-muted);
    cursor: not-allowed;
  }
`;

export const SocialSection = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

export const SocialTitle = styled.h3`
  color: var(--text-primary);
  font-size: 1.25rem;
  margin-bottom: 1rem;
  font-weight: 700;

  svg {
    color: var(--brand);
  }
`;

export const SocialLinks = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
`;

export const SocialLink = styled.a`
  background: transparent;
  color: var(--text-secondary);
  width: 44px;
  height: 44px;
  border-radius: var(--radius-control);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all 0.18s ease;
  text-decoration: none;
  border: 1px solid var(--border-strong);

  &:hover {
    color: var(--brand);
    border-color: var(--brand);
  }
`;

export const ValuesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
`;

export const ValueItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.02);
  border-radius: var(--radius-card);
  border: 1px solid var(--border);
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
  }
`;

export const ValueIcon = styled.div`
  color: var(--brand);
  font-size: 1.4rem;
  margin-top: 0.2rem;
`;

export const ValueContent = styled.div`
  flex: 1;
`;

export const ValueTitle = styled.h4`
  color: var(--text-primary);
  font-size: 1.05rem;
  margin-bottom: 0.5rem;
  font-weight: 700;
`;

export const ValueDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.9rem;
  line-height: 1.5;
`;

export const ContactCardStyled = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 1.75rem;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: var(--border-strong);
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

export const ContactIconStyled = styled.div`
  color: var(--brand);
  font-size: 1.75rem;
  margin-bottom: 1rem;
`;

export const ContactTitleStyled = styled.h3`
  color: var(--text-primary);
  font-size: 1.2rem;
  margin-bottom: 1.25rem;
  font-weight: 700;
`;

export const ContactInfoStyled = styled.div`
  color: var(--text-secondary);
  line-height: 1.8;
  font-size: 0.95rem;

  br {
    margin-bottom: 0.5rem;
  }

  a {
    color: var(--brand);
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;
